import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./Eligibility.css";
import Seo from "../components/Seo";

const applicantProfiles = {
  salaried: {
    label: "Salaried Applicant",
    shortLabel: "Salaried",
    icon: "bi bi-briefcase",
    description:
      "Document checklist for salaried home-loan applicants and co-applicants.",
    documentGroups: [
      {
        id: "salaried-identity",
        icon: "bi bi-person-vcard",
        title: "Proof of Identity",
        description:
          "Submit any acceptable identity document requested by the lender.",
        items: [
          "Aadhaar Card",
          "PAN Card",
          "Passport",
          "Driving Licence",
          "Voter ID Card",
        ],
      },
      {
        id: "salaried-age",
        icon: "bi bi-calendar-check",
        title: "Proof of Age",
        description:
          "Documents used to establish the applicant’s date of birth.",
        items: [
          "Birth Certificate",
          "Passport",
          "Aadhaar Card",
          "Driving Licence",
          "Class 10 Mark Sheet",
          "Bank passbook containing date of birth, where accepted",
        ],
      },
      {
        id: "salaried-address",
        icon: "bi bi-geo-alt",
        title: "Proof of Residence",
        description:
          "Current and permanent-address documents may be required.",
        items: [
          "Aadhaar Card",
          "Passport",
          "Driving Licence",
          "Voter ID Card",
          "Registered Rent Agreement",
          "Recent Electricity, Telephone or Utility Bill",
          "Ration Card, where accepted",
          "Letter issued by a recognised public authority, where accepted",
        ],
      },
      {
        id: "salaried-income",
        icon: "bi bi-cash-stack",
        title: "Income Documents",
        description:
          "Documents used to assess regular income and repayment capacity.",
        items: [
          "Latest 3 months’ salary slips",
          "Latest 6 months’ salary-account bank statements",
          "Form 16",
          "Income Tax Returns, where required",
          "Employment certificate",
          "Increment or promotion letter, where applicable",
        ],
      },
      {
        id: "salaried-employment",
        icon: "bi bi-building-check",
        title: "Employment Documents",
        description:
          "Documents confirming current employment and work continuity.",
        items: [
          "Employee Identity Card",
          "Appointment Letter",
          "Employment Contract",
          "Confirmation Letter",
          "Latest Employment Certificate",
          "Previous employment details, where required",
        ],
      },
      {
        id: "salaried-banking",
        icon: "bi bi-bank",
        title: "Banking and Existing Loan Documents",
        description:
          "Statements that show banking history and existing obligations.",
        items: [
          "Latest 6 months’ bank statements",
          "Statements of existing loans",
          "Sanction letters of ongoing loans",
          "Repayment schedule of existing loans",
          "Credit-card statements, where requested",
          "Proof of own contribution",
        ],
      },
      {
        id: "salaried-property",
        icon: "bi bi-house-check",
        title: "Property Documents",
        description:
          "Property documents vary according to purchase, resale or construction.",
        items: [
          "Agreement for Sale",
          "Allotment Letter or Buyer’s Agreement",
          "Sale Deed or Title Deeds",
          "Payment receipts issued by developer or seller",
          "Approved building plan",
          "Property tax receipt",
          "No-encumbrance or title-clearance documents",
          "Construction estimate from architect or civil engineer, where applicable",
        ],
      },
      {
        id: "salaried-other",
        icon: "bi bi-folder-check",
        title: "Additional Documents",
        description:
          "Additional documents may be requested according to the case.",
        items: [
          "Passport-size photographs of applicants and co-applicants",
          "Completed loan application form",
          "Processing-fee cheque or payment proof",
          "Relationship proof for co-applicant",
          "Signature verification",
          "Additional lender-specific declarations",
        ],
      },
    ],
  },

  selfEmployed: {
    label: "Self-Employed Applicant",
    shortLabel: "Self-Employed",
    icon: "bi bi-person-workspace",
    description:
      "Document checklist for self-employed professionals and business owners.",
    documentGroups: [
      {
        id: "self-identity",
        icon: "bi bi-person-vcard",
        title: "Proof of Identity",
        description:
          "Submit acceptable KYC and identity documents.",
        items: [
          "Aadhaar Card",
          "PAN Card",
          "Passport",
          "Driving Licence",
          "Voter ID Card",
        ],
      },
      {
        id: "self-age",
        icon: "bi bi-calendar-check",
        title: "Proof of Age",
        description:
          "Documents confirming the applicant’s date of birth.",
        items: [
          "Birth Certificate",
          "Passport",
          "Aadhaar Card",
          "Driving Licence",
          "Class 10 Mark Sheet",
          "Bank passbook containing date of birth, where accepted",
        ],
      },
      {
        id: "self-address",
        icon: "bi bi-geo-alt",
        title: "Proof of Residence",
        description:
          "Residential-address documents for applicants and co-applicants.",
        items: [
          "Aadhaar Card",
          "Passport",
          "Driving Licence",
          "Voter ID Card",
          "Registered Rent Agreement",
          "Recent Electricity, Telephone or Utility Bill",
          "Ration Card, where accepted",
        ],
      },
      {
        id: "self-business",
        icon: "bi bi-shop",
        title: "Business Proof",
        description:
          "Documents establishing the existence and nature of the business.",
        items: [
          "GST Registration Certificate",
          "Shop and Establishment Certificate",
          "Udyam Registration",
          "Partnership Deed",
          "Certificate of Incorporation",
          "Memorandum and Articles of Association",
          "Professional Practice Certificate, where applicable",
          "Business-address proof",
        ],
      },
      {
        id: "self-continuity",
        icon: "bi bi-clock-history",
        title: "Proof of Business Continuity",
        description:
          "Documents showing how long the business or profession has operated.",
        items: [
          "Income Tax Returns for the latest 3 years",
          "GST returns",
          "Previous registration certificates",
          "Business bank statements",
          "Client invoices or contracts, where required",
          "Professional licence or membership certificate",
        ],
      },
      {
        id: "self-income",
        icon: "bi bi-graph-up-arrow",
        title: "Income and Financial Documents",
        description:
          "Financial records used to evaluate profitability and repayment capacity.",
        items: [
          "Income Tax Returns for the latest 3 years",
          "Audited Balance Sheet",
          "Profit and Loss Account",
          "Computation of Income",
          "Tax Audit Report, where applicable",
          "Latest 12 months’ business bank statements",
          "Latest 6 months’ personal bank statements",
        ],
      },
      {
        id: "self-obligations",
        icon: "bi bi-credit-card",
        title: "Existing Loan and Liability Documents",
        description:
          "Documents relating to current business and personal liabilities.",
        items: [
          "Statements of existing loans",
          "Sanction letters of ongoing loans",
          "Repayment schedules",
          "Overdraft or cash-credit statements",
          "Credit-card statements, where required",
          "Details of business liabilities",
        ],
      },
      {
        id: "self-property",
        icon: "bi bi-house-check",
        title: "Property Documents",
        description:
          "Documents for the property being purchased, constructed or refinanced.",
        items: [
          "Agreement for Sale",
          "Allotment Letter or Buyer’s Agreement",
          "Sale Deed or Title Deeds",
          "Payment receipts issued by developer or seller",
          "Approved building plan",
          "Property tax receipt",
          "No-encumbrance or title-clearance documents",
          "Construction estimate from architect or civil engineer, where applicable",
        ],
      },
      {
        id: "self-other",
        icon: "bi bi-folder-check",
        title: "Additional Documents",
        description:
          "Other documents that may be requested during lender assessment.",
        items: [
          "Passport-size photographs of applicants and co-applicants",
          "Completed loan application form",
          "Processing-fee cheque or payment proof",
          "Proof of own contribution",
          "Relationship proof for co-applicant",
          "Additional lender-specific declarations",
        ],
      },
    ],
  },
};

const eligibilityFactors = [
  {
    icon: "bi bi-person-check",
    title: "Applicant Profile",
    description:
      "Employment or business stability, age and overall financial profile.",
  },
  {
    icon: "bi bi-cash-coin",
    title: "Income Capacity",
    description:
      "Monthly income, business profitability and existing financial obligations.",
  },
  {
    icon: "bi bi-speedometer2",
    title: "Credit Profile",
    description:
      "Credit score, repayment history and current credit exposure.",
  },
  {
    icon: "bi bi-house-check",
    title: "Property Assessment",
    description:
      "Property value, legal title, technical verification and lender policy.",
  },
];

const Eligibility = () => {
  const [activeProfile, setActiveProfile] = useState("salaried");
  const [openGroup, setOpenGroup] = useState(
    applicantProfiles.salaried.documentGroups[0].id
  );
  const [checkedDocuments, setCheckedDocuments] = useState({});

  const selectedProfile = applicantProfiles[activeProfile];

  const totalDocuments = useMemo(
    () =>
      selectedProfile.documentGroups.reduce(
        (total, group) => total + group.items.length,
        0
      ),
    [selectedProfile]
  );

  const completedDocuments = useMemo(
    () =>
      Object.entries(checkedDocuments).filter(
        ([documentKey, isChecked]) =>
          documentKey.startsWith(`${activeProfile}-`) && isChecked
      ).length,
    [checkedDocuments, activeProfile]
  );

  const completionPercentage =
    totalDocuments > 0
      ? Math.round((completedDocuments / totalDocuments) * 100)
      : 0;

  const handleProfileChange = (profileKey) => {
    setActiveProfile(profileKey);
    setOpenGroup(applicantProfiles[profileKey].documentGroups[0].id);
  };

  const handleDocumentToggle = (groupId, documentIndex) => {
    const documentKey = `${activeProfile}-${groupId}-${documentIndex}`;

    setCheckedDocuments((currentDocuments) => ({
      ...currentDocuments,
      [documentKey]: !currentDocuments[documentKey],
    }));
  };

  const clearChecklist = () => {
    setCheckedDocuments((currentDocuments) => {
      const remainingDocuments = {};

      Object.entries(currentDocuments).forEach(([key, value]) => {
        if (!key.startsWith(`${activeProfile}-`)) {
          remainingDocuments[key] = value;
        }
      });

      return remainingDocuments;
    });
  };

  return (
    <>
      <Seo
        title="Home Loan Eligibility and Documents | MONAYRA"
        description="Review home-loan eligibility factors and document requirements for salaried and self-employed applicants with MONAYRA (OPC) PRIVATE LIMITED."
        keywords="home loan eligibility, home loan documents, salaried home loan documents, self employed home loan documents, mortgage eligibility"
        canonical="https://www.mortbuzz.in/eligibility"
      />

      <main className="monayra-eligibility-page">
        <section className="eligibility-page-hero">
          <div className="eligibility-page-hero-overlay"></div>

          <div
            className="eligibility-page-glow eligibility-page-glow-left"
            aria-hidden="true"
          ></div>

          <div
            className="eligibility-page-glow eligibility-page-glow-right"
            aria-hidden="true"
          ></div>

          <div className="eligibility-page-container eligibility-page-hero-grid">
            <div className="eligibility-page-hero-content">
              <span className="eligibility-page-eyebrow">
                Home Loan Preparation
              </span>

              <h1>
                Understand eligibility and prepare your
                <span> home-loan documents</span>
              </h1>

              <p>
                Review the common documents required for salaried and
                self-employed applicants before beginning your home-loan
                application.
              </p>

              <div className="eligibility-page-hero-actions">
                <Link
                  to="/apply-now"
                  className="eligibility-page-primary-btn"
                >
                  Apply for Home Loan
                  <i className="bi bi-arrow-right"></i>
                </Link>

                <a
                  href="#document-checklist"
                  className="eligibility-page-secondary-btn"
                >
                  <i className="bi bi-list-check"></i>
                  View Checklist
                </a>
              </div>

              <div className="eligibility-page-hero-features">
                <div>
                  <i className="bi bi-person-check"></i>
                  Salaried checklist
                </div>

                <div>
                  <i className="bi bi-person-workspace"></i>
                  Self-employed checklist
                </div>

                <div>
                  <i className="bi bi-check2-square"></i>
                  Interactive progress tracker
                </div>
              </div>
            </div>

            <aside className="eligibility-page-summary-card">
              <span className="eligibility-page-summary-icon">
                <i className={selectedProfile.icon}></i>
              </span>

              <span className="eligibility-page-card-eyebrow">
                Selected Applicant Type
              </span>

              <h2>{selectedProfile.label}</h2>
              <p>{selectedProfile.description}</p>

              <div className="eligibility-page-summary-stats">
                <div>
                  <strong>{selectedProfile.documentGroups.length}</strong>
                  <span>Document groups</span>
                </div>

                <div>
                  <strong>{totalDocuments}</strong>
                  <span>Checklist items</span>
                </div>
              </div>

              <Link
                to="/contact"
                className="eligibility-page-secondary-btn"
              >
                <i className="bi bi-headset"></i>
                Ask Our Team
              </Link>
            </aside>
          </div>
        </section>

        <section className="eligibility-factors-section">
          <div className="eligibility-page-container">
            <div className="eligibility-page-section-header">
              <span className="eligibility-page-eyebrow">
                Basic Eligibility Factors
              </span>

              <h2>
                What lenders generally
                <span> evaluate</span>
              </h2>

              <p>
                Home-loan approval depends on the complete applicant,
                repayment and property profile.
              </p>
            </div>

            <div className="eligibility-factors-grid">
              {eligibilityFactors.map((factor) => (
                <article
                  className="eligibility-factor-card"
                  key={factor.title}
                >
                  <span>
                    <i className={factor.icon}></i>
                  </span>

                  <h3>{factor.title}</h3>
                  <p>{factor.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          id="document-checklist"
          className="eligibility-checklist-section"
        >
          <div className="eligibility-page-container">
            <div className="eligibility-page-section-header">
              <span className="eligibility-page-eyebrow">
                Document Checklist
              </span>

              <h2>
                Choose your applicant
                <span> profile</span>
              </h2>

              <p>
                Mark documents as ready to track your preparation progress.
              </p>
            </div>

            <div className="eligibility-profile-tabs">
              {Object.entries(applicantProfiles).map(
                ([profileKey, profile]) => (
                  <button
                    type="button"
                    key={profileKey}
                    className={
                      activeProfile === profileKey ? "active" : ""
                    }
                    onClick={() => handleProfileChange(profileKey)}
                  >
                    <span>
                      <i className={profile.icon}></i>
                    </span>

                    <div>
                      <strong>{profile.shortLabel}</strong>
                      <small>
                        {profile.documentGroups.length} document groups
                      </small>
                    </div>
                  </button>
                )
              )}
            </div>

            <div className="eligibility-checklist-layout">
              <section className="eligibility-document-panel">
                <div className="eligibility-document-panel-header">
                  <div>
                    <span>{selectedProfile.shortLabel} Applicant</span>
                    <h3>Required document categories</h3>
                  </div>

                  <span className="eligibility-document-count">
                    {selectedProfile.documentGroups.length} categories
                  </span>
                </div>

                <div className="eligibility-accordion-list">
                  {selectedProfile.documentGroups.map((group) => {
                    const isOpen = openGroup === group.id;

                    const groupCompletedCount = group.items.filter(
                      (_, index) =>
                        checkedDocuments[
                          `${activeProfile}-${group.id}-${index}`
                        ]
                    ).length;

                    return (
                      <article
                        className={`eligibility-accordion-item ${
                          isOpen ? "open" : ""
                        }`}
                        key={group.id}
                      >
                        <button
                          type="button"
                          className="eligibility-accordion-trigger"
                          onClick={() =>
                            setOpenGroup(isOpen ? null : group.id)
                          }
                          aria-expanded={isOpen}
                        >
                          <span className="eligibility-group-icon">
                            <i className={group.icon}></i>
                          </span>

                          <span className="eligibility-group-title">
                            <strong>{group.title}</strong>
                            <small>{group.description}</small>
                          </span>

                          <span className="eligibility-group-progress">
                            {groupCompletedCount}/{group.items.length}
                          </span>

                          <span className="eligibility-group-toggle">
                            <i
                              className={
                                isOpen
                                  ? "bi bi-dash-lg"
                                  : "bi bi-plus-lg"
                              }
                            ></i>
                          </span>
                        </button>

                        {isOpen && (
                          <div className="eligibility-accordion-content">
                            <div className="eligibility-document-list">
                              {group.items.map((item, index) => {
                                const documentKey = `${activeProfile}-${group.id}-${index}`;

                                const isChecked =
                                  Boolean(checkedDocuments[documentKey]);

                                return (
                                  <label
                                    className={`eligibility-document-item ${
                                      isChecked ? "checked" : ""
                                    }`}
                                    key={`${group.id}-${item}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() =>
                                        handleDocumentToggle(
                                          group.id,
                                          index
                                        )
                                      }
                                    />

                                    <span className="eligibility-custom-checkbox">
                                      <i className="bi bi-check-lg"></i>
                                    </span>

                                    <span>{item}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              </section>

              <aside className="eligibility-progress-card">
                <span className="eligibility-progress-icon">
                  <i className="bi bi-clipboard2-check"></i>
                </span>

                <span className="eligibility-page-card-eyebrow">
                  Preparation Progress
                </span>

                <h3>{selectedProfile.shortLabel} checklist</h3>

                <div className="eligibility-progress-circle">
                  <div
                    style={{
                      "--eligibility-progress": `${completionPercentage}%`,
                    }}
                  >
                    <span>{completionPercentage}%</span>
                    <small>Ready</small>
                  </div>
                </div>

                <div className="eligibility-progress-details">
                  <div>
                    <span>Documents marked ready</span>
                    <strong>
                      {completedDocuments} / {totalDocuments}
                    </strong>
                  </div>

                  <div>
                    <span>Remaining documents</span>
                    <strong>
                      {Math.max(totalDocuments - completedDocuments, 0)}
                    </strong>
                  </div>
                </div>

                <div className="eligibility-progress-bar">
                  <span
                    style={{
                      width: `${completionPercentage}%`,
                    }}
                  ></span>
                </div>

                <button
                  type="button"
                  className="eligibility-clear-btn"
                  onClick={clearChecklist}
                  disabled={completedDocuments === 0}
                >
                  <i className="bi bi-arrow-counterclockwise"></i>
                  Clear This Checklist
                </button>

                <Link
                  to="/apply-now"
                  className="eligibility-page-primary-btn"
                >
                  Start Loan Application
                  <i className="bi bi-arrow-right"></i>
                </Link>
              </aside>
            </div>

            <div className="eligibility-checklist-note">
              <i className="bi bi-info-circle"></i>

              <p>
                The exact document list varies according to the lender,
                applicant profile, income source, property type and transaction.
                Additional documents may be requested during legal, technical
                or credit assessment.
              </p>
            </div>
          </div>
        </section>

        <section className="eligibility-process-section">
          <div className="eligibility-page-container eligibility-process-grid">
            <div className="eligibility-process-content">
              <span className="eligibility-page-eyebrow">
                What Happens Next?
              </span>

              <h2>
                From document collection to
                <span> loan assessment</span>
              </h2>

              <p>
                Once documents are collected, the application moves through
                lender review, credit assessment, property verification and
                sanction processing.
              </p>

              <div className="eligibility-process-steps">
                {[
                  {
                    number: "01",
                    title: "Document Review",
                    text:
                      "KYC, income, banking and property documents are reviewed for completeness.",
                  },
                  {
                    number: "02",
                    title: "Credit Assessment",
                    text:
                      "The lender evaluates income, obligations, credit score and repayment capacity.",
                  },
                  {
                    number: "03",
                    title: "Property Verification",
                    text:
                      "Legal and technical verification is completed for the proposed property.",
                  },
                  {
                    number: "04",
                    title: "Sanction Decision",
                    text:
                      "The lender decides the eligible amount, rate, tenure and applicable conditions.",
                  },
                ].map((step) => (
                  <article key={step.number}>
                    <span>{step.number}</span>

                    <div>
                      <h3>{step.title}</h3>
                      <p>{step.text}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <aside className="eligibility-help-card">
              <span className="eligibility-help-icon">
                <i className="bi bi-headset"></i>
              </span>

              <span className="eligibility-page-card-eyebrow">
                Document Assistance
              </span>

              <h3>Need help preparing your home-loan file?</h3>

              <p>
                Our team can guide you regarding the general checklist,
                applicant profile, property documents and application process.
              </p>

              <Link
                to="/contact"
                className="eligibility-page-primary-btn"
              >
                Contact Our Team
                <i className="bi bi-arrow-right"></i>
              </Link>

              <Link
                to="/emi-calculator"
                className="eligibility-page-secondary-btn"
              >
                <i className="bi bi-calculator"></i>
                Calculate EMI
              </Link>
            </aside>
          </div>
        </section>

        <section className="eligibility-cta-section">
          <div className="eligibility-page-container">
            <div className="eligibility-cta-card">
              <div>
                <span>Ready to Apply?</span>

                <h2>Begin your home-loan journey with MONAYRA</h2>

                <p>
                  Share your income, property and loan requirement to receive
                  assistance with eligibility, documentation and lender
                  processing.
                </p>
              </div>

              <div className="eligibility-cta-actions">
                <Link
                  to="/apply-now"
                  className="eligibility-page-primary-btn"
                >
                  Apply Now
                  <i className="bi bi-arrow-right"></i>
                </Link>

                <Link
                  to="/contact"
                  className="eligibility-page-secondary-btn"
                >
                  <i className="bi bi-telephone"></i>
                  Speak with Our Team
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="eligibility-page-disclaimer">
          <div className="eligibility-page-container">
            <i className="bi bi-shield-exclamation"></i>

            <p>
              The checklist is indicative and does not constitute a guarantee
              of eligibility or approval. Final document requirements,
              eligible loan amount, interest rate, tenure, fees and approval
              are determined by the selected lender. MONAYRA (OPC) PRIVATE
              LIMITED provides loan assistance and does not guarantee approval.
            </p>
          </div>
        </section>
      </main>
    </>
  );
};

export default Eligibility;